import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, ButtonVariant } from '../../common/Button'
import { HorizontalFormSection } from '../../common/HorizontalFormSection'
import { IconChoices } from '../../common/Icon'
import { ToastType } from '../../common/Toast/toast-type'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useAuthenticatedUserId } from '../../../hooks/useAuthenticatedUserId'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { NUsers } from '../../../interfaces/services/users.interfaces'
import { LanguageListItem } from './LanguageListItem'
import { LocalStorageKeys } from '../../../types/local-storage'
import { getLanguageCode } from '../../../helpers/translations/helpers'
import { patchUser, userQueries } from '../../../api/users'

export const ChangeLanguage = (): React.ReactNode => {
  const { i18n } = useTranslation()
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const [selectedLocale, setSelectedLocale] = useState<string | undefined>(
    undefined
  )
  const userId = useAuthenticatedUserId() as string
  const { data: user } = useQuery(userQueries.getUser(userId))

  const languages = [
    {
      name: 'Svenska',
      description: 'Samtlig information i Boappa angiven på Svenska',
      flag: '🇸🇪',
      translatedName: translate('common.languages.swedish'),
      locale: 'sv-SE',
    },
    {
      name: 'English',
      description: 'The language in Boappa is in English',
      flag: '🇬🇧',
      translatedName: translate('common.languages.english'),
      locale: 'en-EN',
    },
    {
      name: 'Soumi',
      description: 'Boappa kieli on Suomeksi',
      flag: '🇫🇮',
      translatedName: translate('common.languages.finnish'),
      locale: 'fi-FI',
    },
  ]

  const langCode = (lang: string): string => {
    switch (lang) {
      case 'se':
        return 'sv-SE'
      case 'fi':
        return 'fi-FI'
      case 'en':
        return 'en-EN'
      default:
        return 'sv-SE'
    }
  }

  useEffect(() => {
    const getLanguage = (): string => {
      if (!user?.language) {
        return 'se'
      }
      return langCode(user.language)
    }
    const language = getLanguage()
    setSelectedLocale(language || i18n.language)
  }, [user?.language, i18n.language])

  const { mutate: mutatePatchUser, isPending: isUpdatingUser } = useMutation({
    mutationFn: patchUser,
    onSuccess: () => {
      localStorage.setItem(LocalStorageKeys.LOCALE, selectedLocale || 'sv-SE')
      i18n.changeLanguage(selectedLocale || 'sv')
      document.documentElement.lang = selectedLocale || 'sv'
      setToastNotification(
        ToastType.SUCCESS,
        translate('flashMessage.changesSaved')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.changesNotSavedError')
      )
    },
  })
  const onSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault()

    const formData: NUsers.NPatch.IRequestBody = {
      language: getLanguageCode(selectedLocale),
    }
    mutatePatchUser({ id: userId, body: formData })
  }

  const loading = isUpdatingUser

  return (
    <form onSubmit={onSubmit}>
      <HorizontalFormSection
        title={translate('changeLanguage.title')}
        icon={IconChoices.WEBSITE}
        iconPlacement="left"
      >
        {languages.map((_language) => (
          <LanguageListItem
            key={_language.name}
            language={_language.name}
            flag={_language.flag}
            locale={_language.locale}
            selected={_language.locale === selectedLocale}
            setSelectedLocale={setSelectedLocale}
            description={_language.description}
          />
        ))}
      </HorizontalFormSection>
      <HorizontalFormSection title="">
        <div className="flex justify-end">
          <Button
            label={translate('common.actions.save')}
            type="submit"
            loading={loading}
            disabled={loading}
            variant={ButtonVariant.PRIMARY}
          />
        </div>
      </HorizontalFormSection>
    </form>
  )
}
